import { Component, Inject, OnInit, OnDestroy, ViewChild, ElementRef, HostListener, signal, CUSTOM_ELEMENTS_SCHEMA, ViewChildren, QueryList } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormArray, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { ControlMessageService } from 'src/app/service/control-message.service';
import { SnackbarService } from 'src/app/service/snackbar.service';
import { ControlMessage } from 'src/app/interface/control-message';
import { ControlMessageType } from 'src/app/interface/enum-controlMessages-type';
import { EmojiData, EmojiModule } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { LayoutService } from 'src/app/service/layout.service';
import { AccessProfile } from '../../page/company/access-profile/models/access-profile';
import { UserService } from 'src/app/service/user.service';
import { MkgUser } from 'src/app/interface/user';
import { SurveyV2Service } from 'src/app/service/survey-v2.service';
import { Survey } from 'src/app/interface/survey-v2';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { CanAccessPipe } from 'src/app/shared/pipes/can-access.pipe';
import { MatIconModule } from '@angular/material/icon';
import { MatChip } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
@Component({
  selector: 'app-control-message-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CanAccessPipe,
    MatFormFieldModule,
    MatInput,
    MatSelect,
    MatOption,
    MatButtonModule,
    MatIconModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatChip,
    MatDialogContent,
    MatDialogTitle,
    MatDialogActions,
    MatDialogClose,
    EmojiModule
  ],
  templateUrl: './control-message-edit.component.html',
  styleUrls: ['./control-message-edit.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ControlMessageEditComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();

  @ViewChild("observationInput") observationInput: ElementRef<HTMLInputElement>
  @ViewChildren("variationInput") variationInputs: QueryList<ElementRef<HTMLInputElement>>;

  form: FormGroup;
  hours: string[] = Array.from({ length: 24 }, (_, i) => `${i}:00`);
  showEmojiPicker: boolean = false;
  selectedEmoji: EmojiData | undefined;

  showTimeSelector: boolean = false;
  isLaborWarningMessage: boolean = false;
  isPosVenda: boolean = false;
  showDueTitleMessage: boolean = false;
  showCashFlowMessage: boolean = false;
  showStatisticsMessage: boolean = false;
  showChargeMessage: boolean = false;
  showAniversarianteMessage: boolean = false;
  showPesquisaSatisfacao: boolean = false;
  showVariationEmojiPicker: boolean = false;
  currentVariationIndex: number = -1;

  // Tipos de mensagem que exibem o seletor de horário
  timeSelectorTypes: ControlMessageType[] = [
    ControlMessageType.Aniversariante,
    ControlMessageType.PosVenda,
    ControlMessageType.LaborWarningMessage,
    ControlMessageType.DueTitleMessage,
    ControlMessageType.CashFlow,
    ControlMessageType.Statistics,
    ControlMessageType.Charge,
    ControlMessageType.PesquisaDeSatisfacao
  ];

  // Definindo opções estáticas para o campo type
  typeOptions: ITypeOption[] = [
    { id: ControlMessageType.Aniversariante, label: 'Aniversariante' },
    { id: ControlMessageType.AbrirOrcamento, label: 'Abrir orçamento' },
    { id: ControlMessageType.SolicitacaoAtualizacao, label: 'Solicitação de autorização' },
    { id: ControlMessageType.AbrirOS, label: 'Abrir os' },
    { id: ControlMessageType.FecharOS, label: 'Fechar os' },
    { id: ControlMessageType.DueTitleMessage, label: "Vencimento de título" },
    { id: ControlMessageType.CashFlow, label: "Fluxo de caixa" },
    { id: ControlMessageType.Statistics, label: "Indicadores" },
    { id: ControlMessageType.Charge, label: "Cobrança" },
    { id: ControlMessageType.PesquisaDeSatisfacao, label: "Pesquisa de Satisfação" }
  ];

  profile: AccessProfile;

  // Pesquisas disponíveis para Pesquisa de Satisfação
  availableSurveys: Survey[] = [];

  availableVariables: { key: string; description: string; }[] = [];
  showAvailableVariables: boolean = false;
  exampleMessage: string = '';
  filteredUsers = signal<MkgUser[]>([]);
  selectedRecipients = signal<MkgUser[]>([]);

  private readonly messageTemplates = {
    [ControlMessageType.Aniversariante]: {
      variables: [
        { key: '{{nome}}', description: 'Nome do cliente' },
        { key: '{{empresa}}', description: 'Nome da empresa' },
        { key: '{{telefone}}', description: 'Telefone da empresa' }
      ],
      example: 'Feliz aniversário {{nome}}! A equipe da {{empresa}} deseja muitas felicidades! Em caso de dúvidas, entre em contato conosco: {{telefone}}'
    },
    [ControlMessageType.DueTitleMessage]: {
      variables: [
        { key: '{{nome}}', description: 'Nome do usuário' },
        { key: '{{empresa}}', description: 'Nome da empresa' },
        { key: '{{titulos}}', description: 'Lista de títulos com número, valor e vencimento' }
      ],
      example: 'Olá {{nome}}, os seguintes títulos vencerão em 3 dias:\n\n{{titulos}}'
    },
    [ControlMessageType.Charge]: {
      variables: [
        { key: '{{nome}}', description: 'Nome do usuário' },
        { key: '{{empresa}}', description: 'Nome da empresa' },
        { key: '{{titulos}}', description: 'Lista de títulos com número, valor, vencimento e dias em atraso' }
      ],
      example: 'Olá {{nome}}, os seguintes títulos estão em atraso:\n\n{{titulos}}'
    },
    [ControlMessageType.PesquisaDeSatisfacao]: {
      variables: [
        { key: '{{nome}}', description: 'Nome do cliente' },
        { key: '{{link}}', description: 'Link do portal do cliente' }
      ],
      example: 'Olá {{nome}}, seu atendimento foi finalizado! Por favor, avalie nosso serviço acessando o link: {{link}}. Sua opinião é muito importante para nós!'
    }
  };

  usedFrequencies: string[] = [];

  // Opções de frequência para o select
  frequencyOptions = [
    { value: 'daily', label: 'Diário' },
    { value: 'weekly', label: 'Semanal' },
    { value: 'monthly', label: 'Mensal' }
  ];

  // Expor Validators ao template
  Validators = Validators;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { event?: ControlMessage, profile?: AccessProfile } | null,
    private fb: FormBuilder,
    private controlMessageService: ControlMessageService,
    private dialogRef: MatDialogRef<ControlMessageEditComponent>,
    private layoutService: LayoutService,
    private snackbar: SnackbarService,
    private userService: UserService,
    private surveyService: SurveyV2Service
  ) {
    // Inicializa o formulário com os dados da mensagem recebida
    this.form = this.fb.group({
      _id: [data?.event?._id],
      type: [data?.event?.type || undefined, Validators.required],
      message: [data?.event?.message || undefined],
      messageVariations: this.fb.array(data?.event?.messageVariations || ['']),
      time: [data?.event?.time || undefined],
      osDay: [data?.event?.osDay || undefined],
      osFacturedDay: [data?.event?.osFacturedDay || undefined],
      titleMessageDay: [data?.event?.titleMessageDay || undefined],
      recipients: [[]],
      recipientSearch: [''],
      frequency: [data?.event?.frequency || undefined],
      cmv: [data?.event?.cmv || undefined],
      surveyId: [data?.event?.surveyId || undefined]
    });

    if (data?.event?._id) {
      this.changedTypeOption({ id: data?.event?.type, label: "" });
    }

    if (data.profile) {
      this.profile = data.profile;
    }

    dialogRef.backdropClick().pipe(takeUntil(this._destroy$)).subscribe(() => {
      dialogRef.close();
    })
  }

  ngOnInit() {
    if (this.data.event) {
      const objeto = this.data.event
      this.form.patchValue(objeto);
    }

    if (this.data?.event?.recipients?.length) {
      this.form.get('recipients').setValue(this.data.event.recipients.map(recipient => recipient._id));
      this.selectedRecipients.set(this.data.event.recipients);
    }

    // Adicionar o message ao messageVariations se houver message mas não messageVariations
    if (this.form.get('type').value === ControlMessageType.Aniversariante) {
      const message = this.form.get('message').value;
      const variations = this.messageVariations;

      // Se temos um message e não temos messageVariations (ou só temos uma vazia)
      if (message && message.trim() !== '' &&
          (variations.length === 0 ||
           (variations.length === 1 && (!variations.at(0).value || variations.at(0).value.trim() === '')))) {

        // Se o array estiver vazio, adicione um controle, senão use o primeiro
        if (variations.length === 0) {
          variations.push(this.fb.control(message));
        } else {
          variations.at(0).setValue(message);
        }

        // Opcional: limpar o campo message original se desejar
        // this.form.get('message').setValue('');
      }
    }

    this.form.get('recipientSearch').valueChanges.pipe(
      takeUntil(this._destroy$),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(search => this.userService.searchUsers(search))
    ).subscribe(users => {
      this.filteredUsers.set(users);
    });

    // Carregar frequências já utilizadas
    this.loadUsedFrequencies();

    // Carregar pesquisas disponíveis
    this.loadAvailableSurveys();

    // Adiciona listener para quando o tipo mudar
    this.form.get('type').valueChanges.pipe(
      takeUntil(this._destroy$)
    ).subscribe(type => {
      if (type === ControlMessageType.Statistics) {
        this.loadUsedFrequencies();
      }

      // Se o tipo mudar para Aniversariante, mova o message para messageVariations se necessário
      if (type === ControlMessageType.Aniversariante) {
        const message = this.form.get('message').value;
        const variations = this.messageVariations;

        if (message && message.trim() !== '' &&
            (variations.length === 0 ||
             (variations.length === 1 && (!variations.at(0).value || variations.at(0).value.trim() === '')))) {

          if (variations.length === 0) {
            variations.push(this.fb.control(message));
          } else {
            variations.at(0).setValue(message);
          }

          // Limpar o campo message original, pois agora usaremos messageVariations
          this.form.get('message').setValue('');
        }
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.showEmojiPicker) {
        this.showEmojiPicker = false;
        this.observationInput.nativeElement.focus();
      } else if (this.showVariationEmojiPicker) {
        this.showVariationEmojiPicker = false;
      } else {
        this.dialogRef.close();
      }
    }
  }

  // Salva a mensagem ao fechar o diálogo
  async save(): Promise<void> {
    if (this.form.valid) {
      const formValue = this.form.getRawValue();

      // Apenas filtra entradas vazias do messageVariations antes de enviar para a API
      if (formValue.messageVariations && Array.isArray(formValue.messageVariations)) {
        formValue.messageVariations = formValue.messageVariations
          .filter(msg => msg !== null && msg !== undefined && msg.trim() !== '');
      }

      try {
        await this.controlMessageService.saveMessage({
          ...formValue,
          _id: this.data?.event?._id || undefined
        });
        this.dialogRef.close(formValue);
      } catch (error) {
        console.error('Erro ao salvar a mensagem', error);
        this.snackbar.error('CONTROL_MESSAGE.ERROR_WHILE_SAVE_MESSAGE');
      }
    } else {
      this.snackbar.error('CONTROL_MESSAGE.FILL_REQUIRED_FIELDS');
    }
  }

  deleteMessage(): void {
    this.layoutService.loader = true;

    const messageId = this.form.get('_id').value;
    this.controlMessageService.deleteMessage(messageId).then(() => {
      this.dialogRef.close(true);
      this.layoutService.loader = false;
    }).catch(error => {
      console.error('Erro ao excluir a mensagem', error);
      this.snackbar.error('Erro ao excluir a mensagem');
    });
  }
  // Retorna o rótulo do tipo com base no ID
  getMessageTypeLabel(type: ControlMessageType): string {
    const selectedType = this.typeOptions.find(option => option.id === type);
    return selectedType ? selectedType.label : '';
  }

  // Retorna o título do diálogo com base no tipo selecionado
  getDialogTitle(): string {
    const type = this.form.get('type').value;
    if (type) {
      return this.getMessageTypeLabel(type);
    } else {
      return 'Mensagem WhatsApp';
    }
  }

  setShowAniversarianteMessage(typeValue: ControlMessageType) {
    this.showAniversarianteMessage = typeValue === ControlMessageType.Aniversariante;
  }

  setShowPesquisaSatisfacao(typeValue: ControlMessageType) {
    this.showPesquisaSatisfacao = typeValue === ControlMessageType.PesquisaDeSatisfacao;
  }

  changedTypeOption(event: ITypeOption) {
    this.setShowTimeSelector(event.id);
    this.setIsPosVenda(event.id);
    this.setShowMessageInput(event.id);
    this.setAvailableVariables(event.id);
    this.setShowDueTitleMessage(event.id);
    this.setShowCashFlowMessage(event.id);
    this.setShowStatisticsMessage(event.id);
    this.setShowChargeMessage(event.id);
    this.setRequiredFields(event.id);
    this.setShowAniversarianteMessage(event.id);
    this.setShowPesquisaSatisfacao(event.id);
    // Se o tipo for Statistics, carrega as frequências já utilizadas
    if (event.id === ControlMessageType.Statistics) {
      this.loadUsedFrequencies();
    }
  }

  // Verifica se deve exibir o seletor de horário
  setShowTimeSelector(typeValue: ControlMessageType) {
    this.showTimeSelector = this.timeSelectorTypes.includes(typeValue);
  }

  setShowDueTitleMessage(typeValue: ControlMessageType) {
    this.showDueTitleMessage = typeValue === ControlMessageType.DueTitleMessage;
  }

  setShowStatisticsMessage(typeValue: ControlMessageType) {
    this.showStatisticsMessage = typeValue === ControlMessageType.Statistics;
  }

  setShowCashFlowMessage(typeValue: ControlMessageType) {
    this.showCashFlowMessage = typeValue === ControlMessageType.CashFlow;
  }

  setShowChargeMessage(typeValue: ControlMessageType) {
    this.showChargeMessage = typeValue === ControlMessageType.Charge;
  }

  // Verifica se deve exibir o campo de dias após fechamento
  setIsPosVenda(typeValue: ControlMessageType) {
    this.isPosVenda = typeValue === ControlMessageType.PosVenda;

    const messageControl = this.form.get('message');
    const osDay = this.form.get("osDay");

    messageControl.setValue(undefined);
    osDay.setValue(undefined);
  }

  setShowMessageInput(typeValue: ControlMessageType) {
    const isLaborWarningMessage = typeValue === ControlMessageType.LaborWarningMessage;
    this.isLaborWarningMessage = isLaborWarningMessage;
    this.handleLaborWarningMessage(isLaborWarningMessage);
  }

  handleLaborWarningMessage(isLaborWarningMessage: boolean) {
    const messageControl = this.form.get('message');
    const time = this.form.get("time");

    messageControl.setValue(undefined);
    time.setValue(undefined);
  }

  toggleEmojiPicker(): void {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  toggleVariationEmojiPicker(index: number): void {
    this.currentVariationIndex = index;
    this.showVariationEmojiPicker = !this.showVariationEmojiPicker;
    // Fechar o outro picker se estiver aberto
    if (this.showVariationEmojiPicker) {
      this.showEmojiPicker = false;
    }
  }

  /**
   * Adiciona um emoji, seja ao campo message ou a uma variação
   * @param $event Evento do componente emoji-mart
   */
  addEmoji($event: any): void {
    this.selectedEmoji = $event.emoji;

    // Se estamos em modo de variação, adiciona o emoji à variação selecionada
    if (this.showVariationEmojiPicker && this.currentVariationIndex >= 0) {
      const variations = this.messageVariations;
      const currentValue = variations.at(this.currentVariationIndex).value || '';
      variations.at(this.currentVariationIndex).setValue(currentValue + $event.emoji.native);

      // Fechar o seletor após adicionar
      this.showVariationEmojiPicker = false;
    }
    // Caso contrário, adiciona ao campo message principal
    else {
      const currentMessage = this.form.get('message').value ?? "";
      this.form.get('message').setValue(currentMessage + $event.emoji.native);
      this.observationInput?.nativeElement.focus();
      this.toggleEmojiPicker();
    }
  }

  addEmojiToVariation($event: any): void {
    this.selectedEmoji = $event.emoji;

    if (this.currentVariationIndex >= 0) {
      const variations = this.messageVariations;
      const currentValue = variations.at(this.currentVariationIndex).value || '';
      variations.at(this.currentVariationIndex).setValue(currentValue + $event.emoji.native);

      // Fechar o seletor após adicionar
      this.showVariationEmojiPicker = false;
    }
  }

  private setAvailableVariables(type: ControlMessageType): void {
    const template = this.messageTemplates[type];

    this.availableVariables = template?.variables || [];
    this.showAvailableVariables = Boolean(this.availableVariables.length);
    this.exampleMessage = template?.example || '';
  }

  insertVariable(variable: string): void {
    if (this.showAniversarianteMessage) return;
    const messageControl = this.form.get('message');
    const currentValue = messageControl.value || '';
    const cursorPosition = this.observationInput.nativeElement.selectionStart;

    const newValue = currentValue.slice(0, cursorPosition) +
      variable +
      currentValue.slice(cursorPosition);

    messageControl.setValue(newValue);
  }


  /**
   * Insere uma variável específica em uma variação na posição do cursor
   */
  insertVariableInVariation(variable: string, index: number): void {
    const variations = this.messageVariations;
    const currentValue = variations.at(index).value || '';

    // Obtém o input correto da QueryList
    const inputElements = this.variationInputs.toArray();
    const inputElement = inputElements[index]?.nativeElement;

    if (inputElement) {
      const cursorPosition = inputElement.selectionStart || 0;
      const newValue = currentValue.slice(0, cursorPosition) +
        variable +
        currentValue.slice(cursorPosition);

      variations.at(index).setValue(newValue);

      // Define o foco de volta no input e posiciona o cursor após a variável inserida
      setTimeout(() => {
        inputElement.focus();
        const newPosition = cursorPosition + variable.length;
        inputElement.setSelectionRange(newPosition, newPosition);
      }, 0);
    } else {
      // Fallback
      variations.at(index).setValue(currentValue + variable);
    }
  }

  selectUser(user: MkgUser): void {
    const currentRecipients = this.selectedRecipients();
    if (!currentRecipients.some(r => r._id === user._id)) {
      this.selectedRecipients.set([...currentRecipients, user]);
      this.form.patchValue({
        recipients: [...currentRecipients, user]
      });
    }
    this.form.get('recipientSearch').setValue('');
  }

  removeUser(user: MkgUser): void {
    const currentRecipients = this.selectedRecipients();
    this.selectedRecipients.set(currentRecipients.filter(r => r._id !== user._id));
    this.form.patchValue({
      recipients: currentRecipients.filter(r => r._id !== user._id)
    });
  }

  private atLeastOneNonEmptyValidator(): any {
    return (formArray: FormArray) => {
      const hasNonEmpty = formArray.controls.some(control =>
        control.value && control.value.trim() !== ''
      );
      return hasNonEmpty ? null : { 'required': true };
    };
  }

  /**
   * Configura os campos obrigatórios baseado no tipo de mensagem
   */
  private setRequiredFields(type: ControlMessageType): void {
    const messageControl = this.form.get('message');
    const timeControl = this.form.get('time');
    const titleMessageDayControl = this.form.get('titleMessageDay');
    const recipientsControl = this.form.get('recipients');
    const frequencyControl = this.form.get('frequency');
    const cmvControl = this.form.get('cmv');
    const osFacturedDayControl = this.form.get('osFacturedDay');
    const surveyIdControl = this.form.get('surveyId');
    const messageVariations = this.form.get('messageVariations') as FormArray;

    // Primeiro limpa todos os validators
    messageControl.clearValidators();
    timeControl.clearValidators();
    titleMessageDayControl.clearValidators();
    recipientsControl.clearValidators();
    frequencyControl.clearValidators();
    cmvControl.clearValidators();
    if (osFacturedDayControl) {
      osFacturedDayControl.clearValidators();
    }
    if (surveyIdControl) {
      surveyIdControl.clearValidators();
    }
    messageVariations.clearValidators();

    // Configura os validators baseado no tipo
    switch (type) {
      case ControlMessageType.DueTitleMessage:
        messageControl.addValidators([Validators.required]);
        titleMessageDayControl.addValidators([Validators.required]);
        recipientsControl.addValidators([Validators.required]);
        timeControl.addValidators([Validators.required]);
        break;

      case ControlMessageType.Aniversariante:
        messageVariations.addValidators([Validators.required, this.atLeastOneNonEmptyValidator()]);
        timeControl.addValidators([Validators.required]);
        break;

      case ControlMessageType.AbrirOS:
        messageControl.addValidators([Validators.required]);
        break;

      case ControlMessageType.FecharOS:
        messageControl.addValidators([Validators.required]);
        break;

      case ControlMessageType.PesquisaDeSatisfacao:
        messageControl.addValidators([Validators.required]);
        timeControl.addValidators([Validators.required]);
        if (osFacturedDayControl) {
          osFacturedDayControl.addValidators([Validators.required]);
        }
        if (surveyIdControl) {
          surveyIdControl.addValidators([Validators.required]);
        }
        break;

      case ControlMessageType.CashFlow:
        timeControl.addValidators([Validators.required]);
        recipientsControl.addValidators([Validators.required]);
        break;

      case ControlMessageType.SolicitacaoAtualizacao:
        messageControl.addValidators([Validators.required]);
        break;

      case ControlMessageType.AbrirOrcamento:
        messageControl.addValidators([Validators.required]);
        break;

      case ControlMessageType.Statistics:
        timeControl.addValidators([Validators.required]);
        frequencyControl.addValidators([Validators.required]);
        cmvControl.addValidators([Validators.required]);
        recipientsControl.addValidators([Validators.required]);

        break;

      case ControlMessageType.Charge:
        messageControl.addValidators([Validators.required]);
        timeControl.addValidators([Validators.required]);
        titleMessageDayControl.addValidators([Validators.required]);
        break;

      default:
        break;
    }

    // Atualiza o estado dos controles
    messageControl.updateValueAndValidity();
    timeControl.updateValueAndValidity();
    titleMessageDayControl.updateValueAndValidity();
    recipientsControl.updateValueAndValidity();
    frequencyControl.updateValueAndValidity();
    cmvControl.updateValueAndValidity();
    if (osFacturedDayControl) {
      osFacturedDayControl.updateValueAndValidity();
    }
    if (surveyIdControl) {
      surveyIdControl.updateValueAndValidity();
    }
    messageVariations.updateValueAndValidity();
  }

  /**
   * Carrega as frequências já utilizadas para mensagens Statistics
   */
  loadUsedFrequencies(): void {
    const currentMessageId = this.form.get('_id').value;
    this.usedFrequencies = this.controlMessageService.getUsedStatisticsFrequencies(currentMessageId);
  }

  /**
   * Carrega as pesquisas disponíveis para Pesquisa de Satisfação
   */
  async loadAvailableSurveys(): Promise<void> {
    try {
      this.availableSurveys = await this.surveyService.getActiveSurveys();
    } catch (error) {
      console.error('Erro ao carregar pesquisas:', error);
      this.snackbar.error('Erro ao carregar pesquisas disponíveis');
    }
  }

  /**
   * Verifica se uma opção de frequência deve ser desabilitada
   * @param frequency A frequência a ser verificada
   * @returns true se a frequência já está em uso e deve ser desabilitada
   */
  isFrequencyDisabled(frequency: string): boolean {
    return this.usedFrequencies.includes(frequency);
  }

  // Getters e métodos para manipular o array de mensagens
  get messageVariations() {
    return this.form.get('messageVariations') as FormArray;
  }

  addMessageVariation() {
    this.messageVariations.push(this.fb.control(''));
  }

  removeMessageVariation(index: number) {
    this.messageVariations.removeAt(index);
  }

}

interface ITypeOption {
  id: ControlMessageType;
  label: string;
}
